<template>
    <div>
        <real-estate-leaflet-map-ortofoto
            v-bind="$props"
            :extraLayers="communalEquipmentLayers"
            :minZoom="minZoom"
            :maxZoom="maxZoom"
            ref="map"
        >
            <!-- PASS ALL SLOTS TO CHILD COMPONENT -->
            <template v-for="(index, name) in $slots" v-slot:[name]>
                <slot :name="name" />
            </template>
            <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
                <slot :name="name" v-bind="data"></slot>
            </template>
        </real-estate-leaflet-map-ortofoto>
    </div>
</template>

<script>
import L from "leaflet";

import commonprops from "./commonprops"
import RealEstateLeafletMapOrtofoto from "./RealEstateLeafletMapOrtofoto"
import { allCommunalEquipmentList, GJIwmsUrl } from "@/components/k-leaflet-map/utils/communal-equipment-list.js"

export default {
    components: {
        RealEstateLeafletMapOrtofoto
    },
    props: {
        ...commonprops,
        // Optional: filter layers by name (array of layer names to include)
        layersToInclude: {
            type: Array,
            default: null
        },
        minZoom: {
            type: Number
        },
        maxZoom: {
            type: Number,
            default: 19
        },
    },
    computed: {
        communalEquipmentLayers: function() {
            let layers = allCommunalEquipmentList;
            
            // Filter layers if layersToInclude is provided
            if (this.layersToInclude && this.layersToInclude.length > 0) {
                layers = layers.filter(l => this.layersToInclude.includes(l.name));
            }
            
            return layers.map(l => ({
                ...l,
                transparent: true,
                url: GJIwmsUrl,
                type: "overlay",
                format: "image/png",
                maxZoom: 19,
            }))
        },
        leafletMapObject: function(){
            return this.$refs.map && this.$refs.map.leafletMapObject ? this.$refs.map.leafletMapObject : null;
        }
    },
    data: function() {
        return {
            communalEquipmentLegendDiv: null,
            communalEquipmentLegendControl: null,
            communalEquipmentLegendContent: null
        }
    },
    methods: {
        getWMSLegendUrl: function(layerName, width = 20, height = 20) {
            // Get WMS GetLegendGraphic URL for a single layer
            const params = new URLSearchParams({
                SERVICE: "WMS",
                VERSION: "1.1.1",
                REQUEST: "GetLegendGraphic",
                FORMAT: "image/png",
                LAYER: layerName.trim(),
                WIDTH: width.toString(),
                HEIGHT: height.toString()
            });
            return `${GJIwmsUrl}?${params.toString()}`;
        },
        getWMSLegendUrlForLayers: function(layers) {
            // Get legend for the first layer in a comma-separated list
            const layerList = layers.split(",");
            if (layerList.length > 0) {
                return this.getWMSLegendUrl(layerList[0].trim());
            }
            return null;
        },
        getVisibleLayersFromLeaflet: function() {
            // Get actual visible layers from Leaflet layer control
            const map = this.leafletMapObject;
            if (!map) return [];
            
            const visibleLayers = [];
            
            // Method 1: Check checkboxes in the DOM (most reliable)
            const controlContainer = document.querySelector('.leaflet-control-layers');
            if (controlContainer) {
                const checkboxes = controlContainer.querySelectorAll('input[type="checkbox"]');
                checkboxes.forEach((checkbox) => {
                    if (checkbox.checked) {
                        const label = checkbox.closest('label');
                        if (label && label.textContent) {
                            const layerName = label.textContent.trim();
                            // Find matching layer config
                            const matchingLayer = this.communalEquipmentLayers.find(l => l.name === layerName);
                            if (matchingLayer) {
                                visibleLayers.push(matchingLayer);
                            }
                        }
                    }
                });
            }
            
            // Method 2: Fallback - check which layers are actually on the map
            if (visibleLayers.length === 0) {
                this.communalEquipmentLayers.forEach((layerConfig) => {
                    // Iterate through all layers on the map
                    map.eachLayer((leafletLayer) => {
                        // Check if this is a WMS tile layer
                        if (leafletLayer.options && leafletLayer.options.layers) {
                            // Check if the layers string matches
                            if (leafletLayer.options.layers === layerConfig.layers) {
                                // Check if layer is on the map (visible)
                                if (map.hasLayer(leafletLayer)) {
                                    visibleLayers.push(layerConfig);
                                }
                            }
                        }
                    });
                });
            }
            
            return visibleLayers;
        },
        updateLegendContent: function() {
            if (!this.communalEquipmentLegendContent) return;
            
            // Clear existing content
            this.communalEquipmentLegendContent.innerHTML = "";
            
            // Get visible layers from Leaflet (actual state, not Vue reactive state)
            const visibleLayers = this.getVisibleLayersFromLeaflet();
            
            if (visibleLayers.length === 0) {
                this.communalEquipmentLegendContent.innerHTML = `
                    <div class="communal-equipment-legend-empty">
                        <p>Ni vidnih slojev</p>
                    </div>
                `;
            } else {
                // Separate layers with complex legends (like "Cesta") from simple ones
                const cestaLayer = visibleLayers.find(l => l.name === "Cesta");
                const simpleLegendLayers = visibleLayers.filter(l => l.name !== "Cesta");
                
                // Only create tabs if "Cesta" is visible
                if (cestaLayer && simpleLegendLayers.length > 0) {
                    // Create tab interface
                    const tabContainer = L.DomUtil.create("div", "communal-equipment-legend-tabs");
                    
                    // Tab buttons
                    const tabButtons = L.DomUtil.create("div", "communal-equipment-legend-tab-buttons");
                    
                    const tabOstalo = L.DomUtil.create("button", "communal-equipment-legend-tab-button active");
                    tabOstalo.innerHTML = "Ostalo";
                    tabOstalo.setAttribute("data-tab", "ostalo");
                    
                    const tabCesta = L.DomUtil.create("button", "communal-equipment-legend-tab-button");
                    tabCesta.innerHTML = "Cesta";
                    tabCesta.setAttribute("data-tab", "cesta");
                    
                    tabButtons.appendChild(tabOstalo);
                    tabButtons.appendChild(tabCesta);
                    
                    // Tab content containers
                    const tabContentOstalo = L.DomUtil.create("div", "communal-equipment-legend-tab-content active");
                    tabContentOstalo.setAttribute("data-tab-content", "ostalo");
                    
                    const tabContentCesta = L.DomUtil.create("div", "communal-equipment-legend-tab-content");
                    tabContentCesta.setAttribute("data-tab-content", "cesta");
                    
                    // Populate "Ostalo" tab with simple legends
                    const gridContainer = L.DomUtil.create("div", "communal-equipment-legend-grid");
                    simpleLegendLayers.forEach((layer) => {
                        const legendItem = L.DomUtil.create("div", "communal-equipment-legend-item");
                        const legendUrl = this.getWMSLegendUrlForLayers(layer.layers);
                        
                        legendItem.innerHTML = `
                            <div class="communal-equipment-legend-item-content">
                                <div class="communal-equipment-legend-icon">
                                    ${legendUrl ? 
                                        `<img src="${legendUrl}" alt="${layer.name}" 
                                              onerror="this.onerror=null; this.style.display='none'; this.nextElementSibling.style.display='block';" 
                                              class="wms-legend-icon" />` : 
                                        ""
                                    }
                                    <div class="communal-equipment-legend-fallback-icon" ${legendUrl ? 'style="display:none;"' : ''}></div>
                                </div>
                                <span class="communal-equipment-legend-label">${layer.name}</span>
                            </div>
                        `;
                        gridContainer.appendChild(legendItem);
                    });
                    tabContentOstalo.appendChild(gridContainer);
                    
                    // Populate "Cesta" tab
                    const legendUrl = this.getWMSLegendUrlForLayers(cestaLayer.layers);
                    const cestaLegendUrl = legendUrl ? this.getWMSLegendUrl(cestaLayer.layers.split(",")[0].trim(), 30, 30) : null;
                    
                    tabContentCesta.innerHTML = `
                        <div class="communal-equipment-legend-complex-content">
                            ${cestaLegendUrl ? 
                                `<img src="${cestaLegendUrl}" alt="${cestaLayer.name} Legend" 
                                      onerror="this.onerror=null; this.style.display='none';" 
                                      class="communal-equipment-legend-complex-image" />` : 
                                `<div class="communal-equipment-legend-complex-fallback">Legend not available</div>`
                            }
                        </div>
                    `;
                    
                    // Tab switching functionality
                    const app = this;
                    [tabOstalo, tabCesta].forEach((button) => {
                        L.DomEvent.disableClickPropagation(button);
                        L.DomEvent.addListener(button, "click", function(e) {
                            L.DomEvent.stopPropagation(e);
                            L.DomEvent.preventDefault(e);
                            const targetTab = button.getAttribute("data-tab");
                            
                            // Update button states
                            tabButtons.querySelectorAll(".communal-equipment-legend-tab-button").forEach(btn => {
                                btn.classList.remove("active");
                            });
                            button.classList.add("active");
                            
                            // Update content visibility
                            tabContainer.querySelectorAll(".communal-equipment-legend-tab-content").forEach(content => {
                                content.classList.remove("active");
                            });
                            tabContainer.querySelector(`[data-tab-content="${targetTab}"]`).classList.add("active");
                        });
                    });
                    
                    tabContainer.appendChild(tabButtons);
                    tabContainer.appendChild(tabContentOstalo);
                    tabContainer.appendChild(tabContentCesta);
                    this.communalEquipmentLegendContent.appendChild(tabContainer);
                    
                } else if (cestaLayer && simpleLegendLayers.length === 0) {
                    // Only "Cesta" is visible - show it directly without tabs
                    const legendUrl = this.getWMSLegendUrlForLayers(cestaLayer.layers);
                    const cestaLegendUrl = legendUrl ? this.getWMSLegendUrl(cestaLayer.layers.split(",")[0].trim(), 30, 30) : null;
                    
                    const cestaSection = L.DomUtil.create("div", "communal-equipment-legend-complex");
                    cestaSection.innerHTML = `
                        <div class="communal-equipment-legend-complex-content">
                            ${cestaLegendUrl ? 
                                `<img src="${cestaLegendUrl}" alt="${cestaLayer.name} Legend" 
                                      onerror="this.onerror=null; this.style.display='none';" 
                                      class="communal-equipment-legend-complex-image" />` : 
                                `<div class="communal-equipment-legend-complex-fallback">Legend not available</div>`
                            }
                        </div>
                    `;
                    this.communalEquipmentLegendContent.appendChild(cestaSection);
                    
                } else {
                    // Only simple layers - show in grid without tabs
                    const gridContainer = L.DomUtil.create("div", "communal-equipment-legend-grid");
                    simpleLegendLayers.forEach((layer) => {
                        const legendItem = L.DomUtil.create("div", "communal-equipment-legend-item");
                        const legendUrl = this.getWMSLegendUrlForLayers(layer.layers);
                        
                        legendItem.innerHTML = `
                            <div class="communal-equipment-legend-item-content">
                                <div class="communal-equipment-legend-icon">
                                    ${legendUrl ? 
                                        `<img src="${legendUrl}" alt="${layer.name}" 
                                              onerror="this.onerror=null; this.style.display='none'; this.nextElementSibling.style.display='block';" 
                                              class="wms-legend-icon" />` : 
                                        ""
                                    }
                                    <div class="communal-equipment-legend-fallback-icon" ${legendUrl ? 'style="display:none;"' : ''}></div>
                                </div>
                                <span class="communal-equipment-legend-label">${layer.name}</span>
                            </div>
                        `;
                        gridContainer.appendChild(legendItem);
                    });
                    this.communalEquipmentLegendContent.appendChild(gridContainer);
                }
            }
        },
        setupLayerControlListeners: function() {
            const app = this;
            const map = this.leafletMapObject;
            if (!map) return;
            
            // Listen to overlay add/remove events from layer control
            map.on('overlayadd', function(e) {
                // Check if this is one of our communal equipment layers
                const layerName = e.name;
                const isCommunalLayer = app.communalEquipmentLayers.some(l => l.name === layerName);
                if (isCommunalLayer) {
                    app.updateLegendContent();
                }
            });
            
            map.on('overlayremove', function(e) {
                // Check if this is one of our communal equipment layers
                const layerName = e.name;
                const isCommunalLayer = app.communalEquipmentLayers.some(l => l.name === layerName);
                if (isCommunalLayer) {
                    app.updateLegendContent();
                }
            });
        },
        addcommunalEquipmentLegend: function(){
            const app = this;
            const map = this.leafletMapObject;
            if (!map) {
                // Map not ready yet, try again after a short delay
                setTimeout(() => {
                    app.addcommunalEquipmentLegend();
                }, 100);
                return;
            }
            
            let legend = L.control({position: "bottomleft"});
            legend.onAdd = function () {
                // Container for toggle button and legend content
                let container = L.DomUtil.create("div", "communal-equipment-legend-container");
                
                // Toggle button
                let toggleButton = L.DomUtil.create("button", "communal-equipment-legend-toggle");
                toggleButton.innerHTML = "Legenda";
                toggleButton.setAttribute("title", "Prikaži/skrij legendo");
                
                // Legend content box
                let legendContent = L.DomUtil.create("div", "communal-equipment-legend-content");
                legendContent.setAttribute("hidden", "hidden");
                
                // Store references
                app.communalEquipmentLegendContent = legendContent;
                app.communalEquipmentLegendDiv = legendContent;
                
                // Initial content
                app.updateLegendContent();
                
                // Toggle functionality
                L.DomEvent.addListener(toggleButton, "click", function (e) {
                    L.DomEvent.stopPropagation(e);
                    if (legendContent.hasAttribute("hidden")) {
                        legendContent.removeAttribute("hidden");
                        toggleButton.classList.add("active");
                    } else {
                        legendContent.setAttribute("hidden", "hidden");
                        toggleButton.classList.remove("active");
                    }
                });
                
                // Prevent map click when clicking on legend
                L.DomEvent.disableClickPropagation(legendContent);
                L.DomEvent.disableScrollPropagation(legendContent);
                
                container.appendChild(toggleButton);
                container.appendChild(legendContent);
                
                return container;
            }
            this.communalEquipmentLegendControl = legend;
            legend.addTo(map);
        },
    },
    mounted() {
        this.addcommunalEquipmentLegend();
        // Wait for map to be fully initialized before setting up listeners
        this.$nextTick(() => {
            // Give Leaflet a moment to set up the layer control
            setTimeout(() => {
                this.setupLayerControlListeners();
                // Initial update
                this.updateLegendContent();
            }, 500);
        });
    }
}
</script>

<style>
    @import "~leaflet/dist/leaflet.css";
    @import "~leaflet-fullscreen/dist/leaflet.fullscreen.css";
    
    /* Communal Equipment Legend Styles */
    .communal-equipment-legend-container {
        background: transparent;
    }
    
    .communal-equipment-legend-toggle {
        background-color: rgba(255, 255, 255, 0.95);
        border: 2px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        padding: 5px 10px;
        font-size: 12px;
        font-weight: 600;
        color: #333;
        cursor: pointer;
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
        transition: all 0.2s ease;
        margin-bottom: 3px;
    }
    
    .communal-equipment-legend-toggle:hover {
        background-color: #fff;
        border-color: rgba(0, 0, 0, 0.3);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    }
    
    .communal-equipment-legend-toggle.active {
        background-color: #4a90e2;
        color: white;
        border-color: #357abd;
    }
    
    .communal-equipment-legend-content {
        background-color: rgba(255, 255, 255, 0.95);
        border: 2px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        padding: 6px;
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
        max-height: 400px;
        overflow-y: auto;
        overflow-x: hidden;
        min-width: 200px;
        max-width: 300px;
    }
    
    .communal-equipment-legend-content::-webkit-scrollbar {
        width: 6px;
    }
    
    .communal-equipment-legend-content::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    
    .communal-equipment-legend-content::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 3px;
    }
    
    .communal-equipment-legend-content::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
    
    .communal-equipment-legend-tabs {
        display: flex;
        flex-direction: column;
    }
    
    .communal-equipment-legend-tab-buttons {
        display: flex;
        gap: 2px;
        margin-bottom: 6px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }
    
    .communal-equipment-legend-tab-button {
        flex: 1;
        padding: 4px 8px;
        font-size: 11px;
        font-weight: 600;
        color: #666;
        background-color: rgba(0, 0, 0, 0.05);
        border: none;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .communal-equipment-legend-tab-button:hover {
        background-color: rgba(0, 0, 0, 0.08);
        color: #333;
    }
    
    .communal-equipment-legend-tab-button.active {
        color: #4a90e2;
        background-color: rgba(74, 144, 226, 0.1);
        border-bottom-color: #4a90e2;
    }
    
    .communal-equipment-legend-tab-content {
        display: none;
    }
    
    .communal-equipment-legend-tab-content.active {
        display: block;
    }
    
    .communal-equipment-legend-complex {
        margin-bottom: 4px;
        padding-bottom: 4px;
    }
    
    .communal-equipment-legend-complex-content {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
    }
    
    .communal-equipment-legend-complex-image {
        max-width: 100%;
        max-height: 120px;
        height: auto;
        width: auto;
        display: block;
        object-fit: contain;
    }
    
    .communal-equipment-legend-complex-fallback {
        font-size: 10px;
        color: #999;
        font-style: italic;
    }
    
    .communal-equipment-legend-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        /* gap: 4px 8px; */
    }
    
    .communal-equipment-legend-item {
        padding: 3px 0;
    }
    
    .communal-equipment-legend-item-content {
        display: flex;
        align-items: center;
        gap: 2px;
    }
    
    .communal-equipment-legend-icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .wms-legend-icon {
        max-width: 18px;
        max-height: 18px;
        object-fit: contain;
    }
    
    .communal-equipment-legend-fallback-icon {
        width: 14px;
        height: 14px;
        background-color: #4a90e2;
        border-radius: 2px;
        border: 1px solid rgba(0, 0, 0, 0.2);
    }
    
    .communal-equipment-legend-label {
        font-size: 11px;
        color: #333;
        line-height: 1.3;
        word-wrap: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
    }
    
    .communal-equipment-legend-empty {
        padding: 6px;
        text-align: center;
        color: #666;
        font-size: 12px;
    }
    
    /* Ensure legend doesn't interfere with map controls */
    .leaflet-bottom.leaflet-left .communal-equipment-legend-container {
        margin-left: 8px;
        margin-bottom: 8px;
    }
</style>